import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Icon from 'src/common/components/icons/Icon';
import { frostedPanelBg } from 'src/common/styles/frostedPanelStyles';
import { dashboardTablePaperSx } from 'src/common/components/table/dashboardTableLayout';
import {
  translateEmailTemplateHelp,
  translateEmailTemplateTitle,
} from 'src/helpers/emailTemplateI18n';
import { EmailTemplateListItem } from 'src/stores/emailTemplatesStore';

interface EmailTemplatesManagerProps {
  templates: EmailTemplateListItem[];
}

function TemplateRow({ item }: { item: EmailTemplateListItem }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const editPath = `/dashboard/system/emails/${encodeURIComponent(item.key)}`;
  const title = translateEmailTemplateTitle(t, item.key, item.adminLabel);
  const help = translateEmailTemplateHelp(t, item.key, item.helpText);

  const openEditor = () => navigate(editPath);

  return (
    <Paper
      variant="outlined"
      role="button"
      tabIndex={0}
      onClick={openEditor}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openEditor();
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        px: 1,
        mb: 0.75,
        bgcolor: theme => frostedPanelBg(theme, 0.85),
        borderColor: 'divider',
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: 'action.hover',
        },
      }}
    >
      <Icon icon="paperPlane" sx={{ fontSize: '3rem', color: 'text.secondary', flexShrink: 0 }} />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {title}
        </Typography>
        {help ? (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
            {help}
          </Typography>
        ) : null}
      </Box>

      {item.hasCustomContent ? (
        <Chip
          size="small"
          label={t('system.emails.customized')}
          variant="outlined"
          color="primary"
          sx={{ display: { xs: 'none', sm: 'flex' } }}
        />
      ) : null}

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={t('participants.edit')}>
          <IconButton size="small" tabIndex={-1} sx={{ pointerEvents: 'none' }}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
}

export default function EmailTemplatesManager({ templates }: EmailTemplatesManagerProps) {
  const { t } = useTranslation();

  return (
    <Paper sx={{ ...dashboardTablePaperSx, p: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('system.emails.listHint')}
      </Typography>
      <Box>
        {templates.map(item => (
          <TemplateRow key={item.key} item={item} />
        ))}
      </Box>
    </Paper>
  );
}
